import {
  ALERT_LEVEL,
  normalizeEnumOrNull,
  numOf,
  recordDrift,
  unwrap,
  type AlertLevel,
  type AlertSummaryItem,
  type AlertSummaryResult,
  type DashboardSnapshot,
  type FunnelNode,
  type FunnelResult,
  type KpiItem,
  type PageResult,
  type RegionBattleItem,
  type TrendPoint,
} from '@/core';
import { http } from './http';
import { PERIOD_LABEL, rangeLabelOfQuery } from '@/core/utils/dashboardRange';

/**
 * 时间窗适配的出口：页面与下钻页从这一处取，算式真源在 `core/utils/dashboardRange.ts`
 * （纯函数、可单测；本模块只把它的结果拼进取数参数，⛔ 不在这里另写一份）。
 */
export {
  dashboardRangeLabel,
  dashboardRangeOfQuery,
  dashboardRangeQuery,
  funnelDrillLinkQuery,
  periodOfQuery,
  periodRangeWindow,
} from '@/core/utils/dashboardRange';
export type { DashboardRangeState, PeriodKey } from '@/core/utils/dashboardRange';

/**
 * Dashboard — §6.2 冻结的是**端点路径**（`GET /dashboard/kpi|funnel|region|trend`、`GET /alerts/summary`），
 * 响应字段名并未冻结。关 mock 联调实测部署版：
 *
 * - `/dashboard/kpi` → `{node,source}` 骨架占位（取数未实现）
 * - `/dashboard/funnel` → 九节点 `MetricSnapshot[]`（批次B 已真化：`period` 窗口与 `/overview` 同源，节点计数在 `metrics` KV 里）
 * - `/dashboard/region` → `{regions:[],note}`（PostGIS 骨架）
 * - `/dashboard/trend` → `{series:[]}`
 * - `/alerts/summary` → `{high,medium,low}` 等级计数，无工单明细
 *
 * 所以本模块是看板族**唯一**的「线格式 → 内部模型」适配点：数组字段按候选名逐个命中，
 * 单条记录的字段名逐个兜底；命中不了就记一条契约漂移并返回空集合 —— 页面走空态，
 * 既不拿假数据填图，也不为后端未定的字段名再猜一层。
 */

export interface DashboardQuery {
  period?: 'today' | 'week' | 'month';
  /** 自定义窗口起日（含），`YYYY-MM-DD`；与 `to` 同时给出才生效 */
  from?: string;
  /** 自定义窗口止日（含），`YYYY-MM-DD` */
  to?: string;
  regionCode?: string;
}

type Raw = Record<string, unknown>;


const KPI_TONES: readonly NonNullable<KpiItem['tone']>[] = ['brand', 'dan', 'ok', 'warn', 'vio', 'ink'];
const KPI_DELTA_TONES = ['ok', 'warn', 'dan', 'mut'] as const;
const ALERT_LEVELS = Object.values(ALERT_LEVEL) as AlertLevel[];

function asRaw(value: unknown): Raw {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : {};
}

/** 取第一个有值的字符串字段（后端字段名未冻结，逐个候选） */
function textOf(raw: Raw, keys: string[], fallback = ''): string {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return fallback;
}

/** 取第一个有值的数值字段 */
function numOfKey(raw: Raw, keys: string[], fallback = 0): number {
  for (const key of keys) {
    const value = raw[key];
    if (value !== undefined && value !== null && value !== '') return numOf(value, fallback);
  }
  return fallback;
}

/** 按「大小写宽松」命中一个键（实测计数键是小写 high/medium/low，枚举口径是大写） */
function numOfLooseKey(raw: Raw, wanted: string): number | null {
  const key = Object.keys(raw).find((item) => item.toUpperCase() === wanted);
  return key ? numOf(raw[key], 0) : null;
}

/** 数组字段候选命中；全部落空即记契约漂移并返回空数组 */
function listOf(payload: unknown, candidates: string[], endpoint: string): Raw[] {
  if (Array.isArray(payload)) return payload.map(asRaw);
  const raw = asRaw(payload);
  for (const key of candidates) {
    const value = raw[key];
    if (Array.isArray(value)) return value.map(asRaw);
  }
  recordDrift(`${endpoint} 响应未给出已知数组字段（候选 ${candidates.join('/')}）`, payload ?? null);
  return [];
}

/**
 * 漏斗容器键候选（响应字段名未冻结）：真取数回 `{stages:[…]}`（`DashboardFunnelDto(object[] Stages)`，
 * PlatformModules.cs:1838；取数腿 `GetFunnel` :2238-2243），旧骨架／旧形状分别用 `nodes`／`items`。
 * 「看板漏斗读腿」（`fetchFunnel`）与「下钻读腿」（`funnelStages`）共用这一组候选，⛔ 不两处各写一份。
 */
const FUNNEL_CONTAINER_KEYS = ['stages', 'nodes', 'items'] as const;

/** 旧九键漏斗形状的键：「下钻读腿」的兜底判据——至少给出一枚才认这形状（见 `funnelStages`） */
const FUNNEL_LEGACY_KEYS = [
  'sampleImport',
  'outboundValid',
  'orderEntered',
  'assigned',
  'accepted',
  'delivered',
  'activated',
  'firstRecharge',
  'retention',
] as const;

/**
 * 在 `keys` 里取第一个「本身存在且是数组」的容器。命中即原样返回（**含空数组**：
 * `{stages:[]}` 是后端明确回的「本窗口没有节点」，⛔ 不当没命中再落别分支）；一枚都没有回 `null`。
 */
function containerArrayOf(payload: unknown, keys: readonly string[]): unknown[] | null {
  const raw = asRaw(payload);
  for (const key of keys) {
    const value = raw[key];
    if (Array.isArray(value)) return value;
  }
  return null;
}

/** §6.2 口径：比率可为 0–1 小数或已带百分号，统一成百分比整数 */
function rateOf(value: number): number {
  return Math.round(value <= 1 ? value * 100 : value);
}

function asKpi(raw: Raw, index: number): KpiItem {
  const tone = textOf(raw, ['tone', 'color']);
  const deltaRaw = asRaw(raw.delta);
  const deltaText = textOf(deltaRaw, ['text', 'label']) || textOf(raw, ['deltaText', 'trend']);
  const deltaTone = textOf(deltaRaw, ['tone']);
  const item: KpiItem = {
    key: textOf(raw, ['key', 'code', 'metric', 'id'], `kpi-${index}`),
    label: textOf(raw, ['label', 'name', 'title'], '—'),
    value: numOfKey(raw, ['value', 'count', 'total', 'qty']),
  };
  const unit = textOf(raw, ['unit']);
  if (unit) item.unit = unit;
  if (typeof raw.precision === 'number') item.precision = raw.precision;
  const toneHit = KPI_TONES.find((candidate) => candidate === tone);
  if (toneHit) item.tone = toneHit;
  if (deltaText) {
    const deltaHit = KPI_DELTA_TONES.find((candidate) => candidate === deltaTone);
    item.delta = { text: deltaText, tone: deltaHit ?? 'mut' };
  }
  const hint = textOf(raw, ['hint', 'description', 'note']);
  if (hint) item.hint = hint;
  if (Array.isArray(raw.spark)) item.spark = (raw.spark as unknown[]).map((point) => numOf(point, 0));
  if (raw.alerting === true) item.alerting = true;
  return item;
}

function asFunnelNode(raw: Raw, index: number, maxValue: number): FunnelNode {
  const value = numOfKey(raw, ['value', 'count', 'total']);
  const node: FunnelNode = {
    key: textOf(raw, ['key', 'code', 'stage'], `n-${index}`),
    label: textOf(raw, ['label', 'name', 'stageName', 'title'], '—'),
    value,
    barPercent: maxValue > 0 ? Math.round((value / maxValue) * 100) : 0,
  };
  const conversion = raw.conversion ?? raw.conversionRate ?? raw.rate;
  if (conversion !== undefined && conversion !== null) node.conversion = rateOf(numOf(conversion, 0));
  const deltaPt = raw.deltaPt ?? raw.delta;
  if (typeof deltaPt === 'number' && Number.isFinite(deltaPt)) {
    node.deltaPt = deltaPt;
    node.deltaTone = deltaPt < 0 ? 'dan' : deltaPt > 0 ? 'ok' : 'warn';
  }
  const note = textOf(raw, ['note', 'remark']);
  if (note) node.note = note;
  return node;
}

function asRegion(raw: Raw): RegionBattleItem {
  const code = [raw.regionCode, raw.code].find((value) => typeof value === 'string' && value.trim());
  const name = [raw.regionName, raw.name, raw.label].find((value) => typeof value === 'string' && value.trim());
  const regionCode = typeof code === 'string' ? code.trim() : '';
  return {
    // 空字符串显式表示缺业务码；名称/key不猜码，UI身份由视图独立投影。
    regionCode,
    regionName: typeof name === 'string' ? name.trim() : regionCode || '地域来源缺失',
    completed: numOfKey(raw, ['completed', 'completeCount', 'done']),
    inFlight: numOfKey(raw, ['inFlight', 'inflight', 'inProgress', 'pending']),
    stalled: numOfKey(raw, ['stalled', 'stagnant', 'overdue']),
    completionRate: rateOf(numOfKey(raw, ['completionRate', 'rate'])),
  };
}

function asTrendPoint(raw: Raw): TrendPoint {
  return {
    date: textOf(raw, ['date', 'day', 'statDate', 'x', 'label'], '—'),
    delivered: numOfKey(raw, ['delivered', 'deliveryCount', 'completed']),
    activated: numOfKey(raw, ['activated', 'activateCount', 'active']),
    firstRecharge: numOfKey(raw, ['firstRecharge', 'rechargeCount', 'firstCharge']),
  };
}

function asAlertItem(raw: Raw, index: number): AlertSummaryItem {
  const ticketNo = textOf(raw, ['ticketNo', 'no', 'code'], `ALERT-${index}`);
  const item: AlertSummaryItem = {
    id: textOf(raw, ['id'], ticketNo),
    ticketNo,
    // 等级缺失按中位呈现：宁可低估也不伪造高危触发看板呼吸
    level: normalizeEnumOrNull(raw.level ?? raw.severity, ALERT_LEVELS) ?? 'MEDIUM',
    title: textOf(raw, ['title', 'summary', 'name'], '未命名告警'),
    ruleName: textOf(raw, ['ruleName', 'rule', 'alertRuleName'], '—'),
    raisedAt: textOf(raw, ['raisedAt', 'createdAt', 'time']),
    status: textOf(raw, ['status', 'ticketStatus'], 'PENDING_CLAIM'),
  };
  const suggestion = textOf(raw, ['aiSuggestion', 'suggestion']);
  if (suggestion) item.aiSuggestion = suggestion;
  return item;
}

/** `{high,medium,low}` → 等级计数；不含任何等级键时返回 null */
function asAlertCounts(payload: unknown): Record<AlertLevel, number> | null {
  const raw = asRaw(payload);
  const picked = ALERT_LEVELS.map((level) => numOfLooseKey(raw, level));
  if (picked.every((value) => value === null)) return null;
  return { HIGH: picked[0] ?? 0, MEDIUM: picked[1] ?? 0, LOW: picked[2] ?? 0 };
}

export async function fetchKpi(query: DashboardQuery = {}): Promise<KpiItem[]> {
  const payload = await unwrap<unknown>(http.get('/dashboard/kpi', { params: query }));
  return listOf(payload, ['items', 'list', 'kpi'], '/dashboard/kpi').map(asKpi);
}

export async function fetchFunnel(query: DashboardQuery = {}): Promise<FunnelResult> {
  const payload = await unwrap<unknown>(http.get('/dashboard/funnel', { params: query }));
  /**
   * ⚠ 判读次序：真取数回的**是数组**（九节点 `MetricSnapshot[]`，计数在 `metrics` KV 里、没有
   * `value/count/total` 键）。若让它落到下面那套通用兜底，`listOf` 会给出 0 行 ⇒ 出「0 节点空表」，
   * 而端点其实是好的 —— 这正是看板「节点下钻」点开像空表的前端那一半。
   * 数组先判；旧对象形状（`{nodes:[…]}`／旧九键）继续走兜底分支，前后端部署次序无关。
   */
  if (Array.isArray(payload)) return mapOverviewFunnel(payload, undefined, String(query.period ?? 'today'));
  /**
   * 〔F-07〕同一条端点的另一条读腿：真取数回 `Result<DashboardFunnelDto>` ⇒ `data` 是
   * `{stages:[九节点 MetricSnapshot]}`（`unwrap` 只剥 `Result` 外壳、不剥 `stages`，client.ts:634）。
   * 容器解出后交给**同一枚** `mapOverviewFunnel`：`MetricSnapshot` 行没有 `value/count/total` 键，
   * 若落进下面的通用兜底（`asFunnelNode`）会得九行 `value=0`＋`label`＝「—」，即拿零顶包。
   */
  const container = containerArrayOf(payload, FUNNEL_CONTAINER_KEYS);
  if (container) {
    return mapOverviewFunnel(container, asRaw(asRaw(payload).bottleneck), String(query.period ?? 'today'));
  }
  const rows = listOf(payload, ['nodes', 'stages', 'items'], '/dashboard/funnel');
  const maxValue = rows.reduce((max, row) => Math.max(max, numOfKey(row, ['value', 'count', 'total'])), 0);
  const raw = asRaw(payload);
  const bottleneckRaw = asRaw(raw.bottleneck);
  const summary = textOf(bottleneckRaw, ['summary', 'message']) || textOf(raw, ['bottleneckSummary']);
  const result: FunnelResult = {
    period: textOf(raw, ['period', 'range'], PERIOD_LABEL[query.period ?? 'today']),
    nodes: rows.map((row, index) => asFunnelNode(row, index, maxValue)),
  };
  if (summary) {
    result.bottleneck = {
      from: textOf(bottleneckRaw, ['from', 'fromStage'], '—'),
      to: textOf(bottleneckRaw, ['to', 'toStage'], '—'),
      summary,
      rowKey: resolveBottleneckRowKey(textOf(bottleneckRaw, ['from', 'fromStage'], '—'), result.nodes),
    };
  }
  return result;
}

export async function fetchRegionBattle(query: DashboardQuery = {}): Promise<RegionBattleItem[]> {
  const payload = await unwrap<unknown>(http.get('/dashboard/region', { params: query }));
  return listOf(payload, ['regions', 'items', 'list'], '/dashboard/region').map(asRegion);
}

function trendRange(payload: unknown, query: DashboardQuery): string {
  return textOf(asRaw(payload), ['range', 'period'], query.period ?? 'today');
}

export async function fetchTrend(query: DashboardQuery = {}): Promise<{ range: string; points: TrendPoint[] }> {
  const payload = await unwrap<unknown>(http.get('/dashboard/trend', { params: query }));
  const rows = listOf(payload, ['points', 'series', 'items'], '/dashboard/trend');
  // 列式 series（{name,data:[]}）与行式点位是两种口径，混用会画出错位折线：显式拒绝并取证
  if (rows.length > 0 && rows.every((row) => Array.isArray(row.data))) {
    recordDrift('/dashboard/trend 的 series 为列式（name+data），需后端给出按日期的行式点位或明确映射规则', rows[0]);
    return { range: trendRange(payload, query), points: [] };
  }
  return { range: trendRange(payload, query), points: rows.map(asTrendPoint) };
}

export async function fetchAlertSummary(limit = 5): Promise<AlertSummaryResult> {
  const payload = await unwrap<unknown>(http.get('/alerts/summary', { params: { limit } }));
  if (Array.isArray(payload)) return { items: payload.map(asRaw).map(asAlertItem), counts: null };
  const raw = asRaw(payload);

  const open = numOf(raw.open, 0);
  const processing = numOf(raw.processing, 0);
  const closedToday = numOf(raw.closedToday, 0);
  if ('open' in raw || 'processing' in raw || 'closedToday' in raw) {
    return { items: [], counts: null, open, processing, closedToday };
  }

  const counts = asAlertCounts(payload);
  const hasDetail = ['items', 'list', 'tickets', 'alerts'].some((key) => Array.isArray(raw[key]));
  if (counts && !hasDetail) {
    recordDrift('/alerts/summary 仅返回 HIGH/MEDIUM/LOW 计数，缺告警明细（工单号/标题/命中规则/AI 建议）', payload);
    return { items: [], counts };
  }
  return { items: listOf(payload, ['items', 'list', 'tickets', 'alerts'], '/alerts/summary').map(asAlertItem), counts };
}

/** 契约 03 号 `DashboardOverview.funnel` 的行（schema 名 `MetricSnapshot`，六枚全 required） */
export interface OverviewFunnelNode {
  /** 「指标节点：样本导入/外呼有效/已录单/已分配/已接单/已交付/已激活/已首充/结算九值之一」（后端给中文原字，前端⛔ 不另立对照表） */
  node: string;
  periodStart?: string;
  periodEnd?: string;
  /** 节点指标 KV：订单八节点给 `orders`，结算节点给 `bills`（都是 numeric 字符串） */
  metrics?: Record<string, unknown>;
  /** 取数源标记：本端联调面见 `live:biz_order` / `live:fin_settle_bill` */
  source?: string;
  snapshotAt?: string;
}

/** 新契约统一看板端点 `GET /dashboard/overview`，合并 KPI/漏斗/区域/趋势/告警 */
export interface OverviewResponse {
  kpis: Record<string, unknown>;
  /** 契约形状＝九节点 `MetricSnapshot[]`；旧后端仍回九键对象，两形状都收（判读见 `funnelStages`） */
  funnel: OverviewFunnelNode[] | Record<string, unknown>;
  bottleneck?: Record<string, unknown>;
  alerts: Raw[];
  trend: { dates: string[]; orders: number[]; completions: number[] };
  /** 窗口回执（additive）：既有后端不回此键，一律按未知处理，缺了就回退本地算式 */
  window?: unknown;
}

export async function fetchOverview(query: DashboardQuery = { period: 'today' }): Promise<OverviewResponse> {
  const payload = await unwrap<OverviewResponse>(http.get<OverviewResponse>('/dashboard/overview', { params: query }));
  return payload;
}

/** 新契约二次跟进工单列表 `GET /dashboard/follow-ups` */
export interface FollowUpQuery {
  page?: number;
  pageSize?: number;
  status?: 'pending' | 'done';
}

export interface FollowUpItem {
  orderId: string;
  customerName: string;
  phone: string;
  lastFollowTime: string;
  nextFollowTime: string;
  note: string;
  assignee: string;
}

export async function fetchFollowUps(query: FollowUpQuery = {}): Promise<PageResult<FollowUpItem>> {
  return unwrap<PageResult<FollowUpItem>>(http.get<PageResult<FollowUpItem>>('/dashboard/follow-ups', { params: query }));
}

/** 顶栏铃铛待办计数 `GET /dashboard/todos` */
export interface DashboardTodoItem {
  type: string;
  title: string;
  count: number;
  deepLink: string;
}

export interface DashboardTodos {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  snapshotAt: string;
  items: DashboardTodoItem[];
}

export async function fetchDashboardTodos(): Promise<DashboardTodos> {
  return unwrap<DashboardTodos>(http.get<DashboardTodos>('/dashboard/todos'));
}

/**
 * 一次拉齐看板快照。
 *
 * 联调实测（2026-09-25）：
 * - `/dashboard/kpi|region|trend` 三端点仍为骨架占位（返回空数组或固定 node），
 *   后端已实现 `/dashboard/overview` 聚合并返回真实 KPI/漏斗/趋势数据。
 * - ⚠ `/dashboard/funnel` **已真化**（批次B）：回九节点 `MetricSnapshot[]`、与 `/overview` 的 `funnel`
 *   同一枚算法；它**不再**回 `{stages:[]}` ⇒ 判读必须认数组（见 `fetchFunnel` 内的次序注释）。
 * - `/alerts/summary` 返回 `{open, processing, closedToday}` 状态计数（非 HIGH/MEDIUM/LOW 等级）。
 *
 * 策略：优先走 overview 聚合端点拿真实数据，映射为 DashboardSnapshot；
 * overview 未覆盖的区域作战仍走独立端点（当前仍为空），告警走独立端点取状态计数。
 */
export async function fetchDashboardSnapshot(query: DashboardQuery = {}): Promise<DashboardSnapshot> {
  const [overview, alertSummary, regionFallback] = await Promise.all([
    fetchOverview(query).catch(() => null),
    fetchAlertSummary().catch(() => ({ items: [], counts: null } as AlertSummaryResult)),
    fetchRegionBattle(query).catch(() => [] as RegionBattleItem[]),
  ]);

  if (overview) {
    // 窗口文案：优先用本次响应回执里的窗口（滚动窗的天数以回执为准），后端未回时回退本地算式；
    // KPI／漏斗／趋势共用这一枚标签与同一份取数参数
    const label = rangeLabelOfQuery(query, responseWindowOf(overview));
    return {
      kpi: mapOverviewKpis(overview.kpis),
      funnel: mapOverviewFunnel(overview.funnel, overview.bottleneck, label),
      region: regionFallback,
      trend: mapOverviewTrend(overview.trend, label),
      alertSummary,
      refreshedAt: new Date().toISOString(),
    };
  }

  // 回落路径：三口独立端点各自按本地算式出文案（fetchFunnel／fetchTrend 内按周期算），与改前逐字一致
  const [kpi, funnel, trend] = await Promise.all([
    fetchKpi(query),
    fetchFunnel(query),
    fetchTrend(query),
  ]);
  return { kpi, funnel, region: regionFallback, trend, alertSummary, refreshedAt: new Date().toISOString() };
}

function mapOverviewKpis(kpis: Record<string, unknown>): KpiItem[] {
  const items: KpiItem[] = [];
  const totalOrders = numOf(kpis.totalOrders, 0);
  const completedOrders = numOf(kpis.completedOrders, 0);
  const alertCount = numOf(kpis.alertCount, 0);

  items.push({ key: 'totalOrders', label: '总订单数', value: totalOrders, tone: 'brand' });
  items.push({ key: 'completedOrders', label: '已完成', value: completedOrders, tone: 'ok' });

  const avgClose = typeof kpis.avgCloseTime === 'string' ? kpis.avgCloseTime : '0h';
  items.push({ key: 'avgCloseTime', label: '平均耗时', value: parseFloat(avgClose) || 0, unit: avgClose.replace(/[0-9.]/g, '') || 'h' });

  const convRate = typeof kpis.conversionRate === 'string' ? kpis.conversionRate : '0%';
  items.push({ key: 'conversionRate', label: '转化率', value: parseFloat(convRate) || 0, unit: '%', precision: 0 });

  items.push({ key: 'alertCount', label: '告警数', value: alertCount, tone: alertCount > 0 ? 'dan' : 'ink' });

  const inProgress = numOf(kpis.inProgress, 0);
  if (inProgress > 0) {
    items.push({ key: 'inProgress', label: '进行中', value: inProgress, tone: 'warn' });
  }

  return items;
}

/** 漏斗节点的一行（`funnelStages` 的输出）：阶梯三枚（key/label/value）＋下钻要出示的来源面。 */
interface FunnelStage {
  key: string;
  label: string;
  value: number;
  /** 单位口径：`orders`＝订单面、`bills`＝账单面（账单折不成订单数）；旧形状兜底给空串 */
  unit: '' | 'orders' | 'bills';
  /** 该节点指标 KV 的原始值（numeric 字符串，原样出示、⛔ 前端不换算） */
  metrics: Record<string, string>;
  source: string;
  snapshotAt: string;
  periodStart: string;
  periodEnd: string;
}

/**
 * 漏斗节点取数：**契约形状优先，旧形状兜底**。
 *
 * - 契约形状（后端 `DashboardOverview.funnel` 按 03 号契约出的 `MetricSnapshot[]`）：九节点
 *   样本导入／外呼有效／已录单／已分配／已接单／已交付／已激活／已首充／结算（出参顺序即九段序），每节点 `metrics` 是 numeric 字符串的 KV —— 订单八节点给
 *   `orders`，结算节点给 `bills`（账单记录面不带订单号，折不成订单数）。节点名与单位都由后端给，
 *   前端⛔ 不另立一张「节点名对照表」：后端加节点时前端不该跟着改一行字面量。
 * - 旧形状（五枚互斥键的对象）：保留为兜底分支 —— 前后端部署次序无关，先发哪边都能出图，
 *   ⛔ 不因为要上新形状就把旧读数丢掉。
 * - 除阶梯三枚外还带出 `unit`/`metrics`/`source`/`snapshotAt`/`periodStart`/`periodEnd` 六枚**来源面**：
 *   下钻页要出示「这枚数从哪张表来、哪一刻的快照」。来源面**同一枚函数**出，⛔ 不在视图侧另取一遍。
 */
function funnelStages(funnel: unknown): FunnelStage[] {
  /**
   * 〔F-07〕先把真取数的容器解掉：`GET /dashboard/funnel` 回 `Result<DashboardFunnelDto>` ⇒ `data` 是
   * `{stages:[九节点 MetricSnapshot]}`（`DashboardFunnelDto(object[] Stages)`，PlatformModules.cs:1838；
   * 取数腿 `GetFunnel` :2238-2243）；`unwrap` 只剥 `Result` 外壳、不剥 `stages`（client.ts:634）。
   * 这一段**必须在**下面的九键兜底分支之前：`{stages:[…]}` 九枚旧键全缺，落到那里会吐九行 0
   * —— 下钻页点开恒空、还把「后端给了九节点」说成「九段全 0」，正是 F-07 的病灶。
   */
  const container = Array.isArray(funnel) ? funnel : containerArrayOf(funnel, FUNNEL_CONTAINER_KEYS);
  if (container) {
    return container
      // 回调显式标 `FunnelStage`：不标的话 `.map()` 的返回是**推**出来的，`unit` 的三值联合会被推成 `string`，
      // 在函数返回处才报错（TS2322），错误点离真因很远。
      .map((raw): FunnelStage => {
        const item = asRaw(raw);
        const metrics = asRaw(item.metrics);
        const node = typeof item.node === 'string' ? item.node : '';
        // 单位判定看键在不在，⛔ 不看值真假：`numOf(null)` 会回 0，拿 0 当「没有这个键」会误判成账单档。
        const hasOrders = Object.prototype.hasOwnProperty.call(metrics, 'orders');
        const value = hasOrders ? numOf(metrics.orders, 0) : numOf(metrics.bills, 0);
        const metricsText: Record<string, string> = {};
        for (const [key, raw] of Object.entries(metrics)) {
          metricsText[key] = typeof raw === 'string' ? raw : raw === null || raw === undefined ? '' : String(raw);
        }
        return {
          key: node,
          label: hasOrders ? node : `${node}（账单）`,
          value,
          unit: hasOrders ? 'orders' : 'bills',
          metrics: metricsText,
          source: typeof item.source === 'string' ? item.source : '',
          snapshotAt: typeof item.snapshotAt === 'string' ? item.snapshotAt : '',
          periodStart: typeof item.periodStart === 'string' ? item.periodStart : '',
          periodEnd: typeof item.periodEnd === 'string' ? item.periodEnd : '',
        };
      })
      .filter((stage) => stage.key !== '');
  }

  // 旧形状兜底：九段漏斗（样本导入/外呼有效/已录单/已分配/已接单/已交付/已激活/已首充/留存中）
  // ⚠ 末段口径与活体腿**不同**：契约形状那段是后端的「结算」（fin_settle_bill 可结算面），这里的
  // `retention` 键沿用旧读数名「留存中」（PRD 第 9 段；RETENTION_CHECK 无生产者）。两条腿各自成立，
  // 故⛔ 不把兜底那段改名去凑活体腿，也⛔ 不拿兜底的 0 当成后端「结算」的读数。
  // ⚠ 这份数据**没有来源面**（无 metrics/source/snapshotAt）⇒ 六枚来源字段如实留空，
  // 下钻页照空渲染「未给」，⛔ 不拿订单面的值替它填上。
  const legacy = asRaw(funnel);
  // 〔F-07〕九键分支只在**确实给了九键之一**时认（`FUNNEL_LEGACY_KEYS`）。三样都不是 ⇒ 形状不认识：
  // 如实降级为空集合（页面走空态、由视图自己渲染「未给」），并记一条契约漂移给联调看 ——
  // ⛔ 不吐九行 0 冒充真实读数（那是拿零顶包），也⛔ 不静默吞掉形状不匹配。
  if (!FUNNEL_LEGACY_KEYS.some((key) => Object.prototype.hasOwnProperty.call(legacy, key))) {
    recordDrift('/dashboard/funnel 的漏斗形状不认识（既非数组、也无 stages/nodes/items 容器、亦非九键旧形状）', funnel ?? null);
    return [];
  }
  const stage = (key: string, label: string, value: number): FunnelStage => ({
    key,
    label,
    value,
    unit: '',
    metrics: {},
    source: '',
    snapshotAt: '',
    periodStart: '',
    periodEnd: '',
  });
  return [
    stage('sampleImport', '样本导入', numOf(legacy.sampleImport, 0)),
    stage('outboundValid', '外呼有效', numOf(legacy.outboundValid, 0)),
    stage('orderEntered', '已录单', numOf(legacy.orderEntered, 0)),
    stage('assigned', '已分配', numOf(legacy.assigned, 0)),
    stage('accepted', '已接单', numOf(legacy.accepted, 0)),
    stage('delivered', '已交付', numOf(legacy.delivered, 0)),
    stage('activated', '已激活', numOf(legacy.activated, 0)),
    stage('firstRecharge', '已首充', numOf(legacy.firstRecharge, 0)),
    stage('retention', '留存中', numOf(legacy.retention, 0)),
  ];
}

/**
 * 看板瓶颈桶名 → 该发光的漏斗行（＝这批单**尚未抵达**的下一格）。
 *
 * 旧判据是模板里的 `bottleneck.from === node.key`，它**恒不亮**：后端 IdentifyBottleneck
 * （code-api src/DeliveryHub.Modules/PlatformModules.cs:2431-2438）为与订单中心页签区分，桶名一律写成
 * 所含状态码的全称——「待分配+公区待领」「已分配+已接单」「配送中及待确认」「已完成」「异常+取消」
 * 「已驳回」；而本端活体漏斗的节点键是节点名（九段：样本导入／外呼有效／已录单／已分配／已接单／已交付／已激活／已首充／结算，见 backend
 * FunnelNodes 的 MetricSnapshot.node，PlatformModules.cs:2368-2384），两套词汇零交集
 * ⇒ 红条照常显示、行却永不发光（t29 用户反馈：点节点下钻看不出发光行）。
 *
 * 表内逐枚写清对应：桶里的单停在「上一格已过、下一格未到」之间，故指向**未到的那一格**。
 * 已完成／异常+取消／已驳回三桶不对应阶梯上的某一格（前者的单已走完阶梯，后两者本不是阶梯段），
 * 值为空串＝不发光（⛔ 不随便挑一行凑高亮）；未登记的桶名回 null 并记一条契约漂移。
 */
export const BOTTLENECK_BUCKET_ROW: Readonly<Record<string, string>> = {
  '待分配+公区待领': '已分配',
  '已分配+已接单': '已交付',
  '配送中及待确认': '已激活',
  '已完成': '',
  '异常+取消': '',
  '已驳回': '',
};

/** 解析瓶颈桶该点亮哪一行；同名直配优先（旧九键形状的 stage 与节点键同名时仍照旧生效）。 */
export function resolveBottleneckRowKey(from: string, nodes: FunnelNode[]): string | null {
  const stage = typeof from === 'string' ? from.trim() : '';
  if (!stage || stage === '—' || nodes.length === 0) return null;
  const direct = nodes.find((node) => node.key === stage || node.label === stage);
  if (direct) return direct.key;
  if (!Object.prototype.hasOwnProperty.call(BOTTLENECK_BUCKET_ROW, stage)) {
    recordDrift(`/dashboard/overview 的瓶颈桶名不在登记表里：${stage}`, { bottleneckStage: stage });
    return null;
  }
  const target = BOTTLENECK_BUCKET_ROW[stage];
  if (!target) return null;
  const row = nodes.find((node) => node.key === target || node.label === target);
  return row ? row.key : null;
}

function mapOverviewFunnel(
  funnel: unknown,
  bottleneck: Record<string, unknown> | undefined,
  period: string,
): FunnelResult {
  const stages = funnelStages(funnel);

  const maxVal = Math.max(...stages.map((s) => s.value), 1);
  const nodes: FunnelNode[] = stages.map((s, i) => {
    const node: FunnelNode = {
      key: s.key,
      label: s.label,
      value: s.value,
      barPercent: Math.round((s.value / maxVal) * 100),
    };
    if (i > 0 && stages[i - 1].value > 0) {
      node.conversion = Math.round((s.value / stages[i - 1].value) * 100);
    }
    return node;
  });

  const result: FunnelResult = {
    period: PERIOD_LABEL[period as keyof typeof PERIOD_LABEL] ?? period,
    nodes,
  };

  if (bottleneck) {
    const stage = typeof bottleneck.stage === 'string' ? bottleneck.stage : '';
    const count = numOf(bottleneck.count, 0);
    const hint = typeof bottleneck.hint === 'string' ? bottleneck.hint : '';
    if (stage && count > 0) {
      result.bottleneck = {
        from: stage,
        to: '',
        summary: hint || `${stage} 积压 ${count} 单`,
        rowKey: resolveBottleneckRowKey(stage, nodes),
      };
    }
  }

  return result;
}

/** 下钻页的一行：阶梯三枚（`FunnelNode`）＋ 该节点的来源面（这枚数从哪张表来、哪一刻的快照）。 */
export interface FunnelDrillNode extends FunnelNode {
  unit: '' | 'orders' | 'bills';
  metrics: Record<string, string>;
  source: string;
  snapshotAt: string;
  periodStart: string;
  periodEnd: string;
}

export interface FunnelDrillResult {
  /** 中文周期标签（与总览共用同一张 `PERIOD_LABEL`） */
  period: string;
  /** 请求用的周期键（`today`/`week`/`month`）：页面的周期切换项与它对齐 */
  periodKey: string;
  nodes: FunnelDrillNode[];
}

/**
 * 节点下钻取数（`GET /dashboard/funnel`）。
 *
 * 这是看板「节点下钻 →」的落点取数口：**与总览共用 `funnelStages` 与同一枚百分比换算**，多出来的
 * 只有来源六枚与窗口起止。下钻页的职责是出示「这枚数是哪张表、什么时点的」，⛔ 不是另算一套转化率：
 * 两处口径一分叉，同一节点就会出现两个数（那正是「下钻与看板对不上」这类缺陷的来处）。
 */
export function mapFunnelDrill(funnel: unknown, period: string, periodKey: string = period): FunnelDrillResult {
  const stages = funnelStages(funnel);
  const maxVal = Math.max(...stages.map((s) => s.value), 1);
  const nodes: FunnelDrillNode[] = stages.map((s, i) => {
    const node: FunnelDrillNode = {
      key: s.key,
      label: s.label,
      value: s.value,
      barPercent: Math.round((s.value / maxVal) * 100),
      unit: s.unit,
      metrics: s.metrics,
      source: s.source,
      snapshotAt: s.snapshotAt,
      periodStart: s.periodStart,
      periodEnd: s.periodEnd,
    };
    // 入口节点无「上一节点」可折 ⇒ 不给 conversion（视图侧按「入口」渲染，⛔ 不填 100% 假装有转化）
    if (i > 0 && stages[i - 1].value > 0) node.conversion = Math.round((s.value / stages[i - 1].value) * 100);
    return node;
  });
  return { period: PERIOD_LABEL[period as keyof typeof PERIOD_LABEL] ?? period, periodKey, nodes };
}

/** 从响应体里取出窗口回执：缺失、非对象、无此键一律给 undefined，交给归一函数兜底 */
function responseWindowOf(payload: unknown): unknown {
  return payload && typeof payload === 'object' ? (payload as Record<string, unknown>).window : undefined;
}

export async function fetchFunnelDrill(query: DashboardQuery = { period: 'today' }): Promise<FunnelDrillResult> {
  const payload = await unwrap<unknown>(http.get('/dashboard/funnel', { params: query }));
  // 副标题口径与看板同一枚：优先用响应回执里的窗口，缺了回退本地算式
  return mapFunnelDrill(payload, rangeLabelOfQuery(query, responseWindowOf(payload)));
}

function mapOverviewTrend(
  trend: { dates: string[]; orders: number[]; completions: number[] },
  period: string,
): { range: string; points: TrendPoint[] } {
  const dates = Array.isArray(trend?.dates) ? trend.dates : [];
  const orders = Array.isArray(trend?.orders) ? trend.orders : [];
  const completions = Array.isArray(trend?.completions) ? trend.completions : [];

  const points: TrendPoint[] = dates.map((date, i) => ({
    date,
    delivered: numOf(completions[i], 0),
    activated: numOf(orders[i], 0),
    firstRecharge: 0,
  }));

  return { range: period, points };
}
